import React, { useState, useEffect } from 'react';
import { chatsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './AnatomyPanel.css';

const SECTION_LABELS = {
  mode: 'Conversation Mode',
  system_prompt: 'System Prompt',
  numerology_context: 'Numerology',
  life_context: 'Life Context',
  relevant_memories: 'Memories',
  ongoing_themes: 'Ongoing Themes',
  session_bridge: 'Session Bridge',
  conversation_summary: 'Conversation Summary',
  memory_highlight: 'Memory Highlight',
  care_context: 'Care Context',
  team_context: 'Team Analysis',
  contact_ei: 'Contact Intelligence',
  recent_messages: 'Chat History'
};

const SECTION_DESCRIPTIONS = {
  mode: 'Active mode chosen by the user — controls Elo\'s tone, depth, and response style for this turn',
  system_prompt: 'The master instruction that defines Elo\'s personality, behavior rules, and how to use your profile data',
  numerology_context: 'Your DOB-based numerology grid, life phase, and personality trait combinations',
  life_context: 'Your active goals, habits, emotional states, and detected behavioral patterns',
  relevant_memories: 'Facts and situations Eloremembers about you from past conversations',
  ongoing_themes: 'Recurring emotional themes that Elohas noticed across your sessions',
  session_bridge: 'Key breakthroughs from previous sessions that Elomay check in on',
  conversation_summary: 'Summary of your earlier messages in this chat, including unresolved threads',
  memory_highlight: 'The most relevant memory or pattern for this specific message',
  care_context: 'Your triggers, the coping tools that work for you, and topics Elohandles gently',
  team_context: 'Team member profiles and dynamics analysis',
  contact_ei: 'Anonymized emotional intelligence data about the contact you\'re discussing',
  recent_messages: 'The last few messages in this conversation for continuity'
};

const AnatomyPanel = ({ chatId, messageId, isOpen, onClose }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    if (!isOpen || !chatId || !messageId) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    chatsApi.getMessageAnatomy(chatId, messageId)
      .then(result => {
        if (!cancelled) setData(result);
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Failed to load anatomy');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, chatId, messageId]);

  if (!isOpen) return null;

  const anatomy = data?.anatomy;
  const raw = data?.raw;
  const isAdmin = user?.is_admin;

  return (
    <>
      <div className={`anatomy-overlay ${isOpen ? 'visible' : ''}`} onClick={onClose} />
      <div className={`anatomy-panel ${isOpen ? 'visible' : ''}`}>
        <div className="anatomy-header">
          <h3>
            <svg className="anatomy-header-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            Answer Anatomy
          </h3>
          <button className="anatomy-close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="anatomy-body">
          {loading && (
            <div className="anatomy-loading">
              <div className="anatomy-loading-spinner" />
              Loading anatomy...
            </div>
          )}

          {error && (
            <div className="anatomy-error">{error}</div>
          )}

          {anatomy && !loading && (
            <>
              {/* Context Badges */}
              <div className="anatomy-section">
                <div className="anatomy-section-header">
                  <svg className="anatomy-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span className="anatomy-section-title">Context Used</span>
                </div>
                <div className="anatomy-badges">
                  {(anatomy.promptSections || []).map(section => (
                    <span key={section} className="anatomy-badge active">
                      <span className="anatomy-badge-dot" />
                      {SECTION_LABELS[section] || section}
                    </span>
                  ))}
                </div>
              </div>

              {/* Attribution Analysis - Why this answer */}
              {anatomy.attribution && (
                <div className="anatomy-section">
                  <div className="anatomy-section-header">
                    <svg className="anatomy-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    <span className="anatomy-section-title">Why This Answer</span>
                  </div>
                  {anatomy.attribution.summary && (
                    <div className="anatomy-attribution-summary">
                      {anatomy.attribution.summary}
                    </div>
                  )}
                  <div className="anatomy-attributions">
                    {(anatomy.attribution.attributions || []).map((attr, i) => (
                      <div key={i} className="anatomy-attribution-item">
                        <div className="anatomy-attribution-header">
                          <span className="anatomy-attribution-num">{i + 1}</span>
                          <span className="anatomy-attribution-section">
                            {SECTION_LABELS[attr.section] || attr.section}
                          </span>
                          {attr.lines?.length > 0 && (
                            <span className="anatomy-attribution-lines">
                              L{attr.lines[0]}{attr.lines.length > 1 ? `–${attr.lines[attr.lines.length - 1]}` : ''}
                            </span>
                          )}
                        </div>
                        <div className="anatomy-attribution-quote">"{attr.quote}"</div>
                        <div className="anatomy-attribution-influence">{attr.influence}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!anatomy.attribution && (
                <div className="anatomy-section">
                  <div className="anatomy-section-header">
                    <svg className="anatomy-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    <span className="anatomy-section-title">Why This Answer</span>
                  </div>
                  <div className="anatomy-attribution-pending">
                    Attribution analysis is processing or not yet available for this message. Try refreshing in a moment.
                  </div>
                </div>
              )}

              {/* Prompt Sections - The actual text sent to AI */}
              {anatomy.promptTexts && Object.keys(anatomy.promptTexts).length > 0 && (
                <div className="anatomy-section">
                  <div className="anatomy-section-header">
                    <svg className="anatomy-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                    <span className="anatomy-section-title">Prompt Sent to AI</span>
                  </div>
                  <div className="anatomy-prompt-hint">
                    These are the exact instructions and context Elo received to construct this answer. Click each section to expand.
                  </div>
                  {(anatomy.promptSections || []).map(section => {
                    const text = anatomy.promptTexts[section];
                    if (!text) return null;
                    return (
                      <details key={section} className="anatomy-prompt-block">
                        <summary className="anatomy-prompt-summary">
                          <span className="anatomy-prompt-name">{SECTION_LABELS[section] || section}</span>
                          <span className="anatomy-prompt-desc">{SECTION_DESCRIPTIONS[section] || ''}</span>
                        </summary>
                        <pre className="anatomy-prompt-text">{text}</pre>
                      </details>
                    );
                  })}
                </div>
              )}

              {/* User Info */}
              {anatomy.user && (
                <div className="anatomy-section">
                  <div className="anatomy-section-header">
                    <svg className="anatomy-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    <span className="anatomy-section-title">User Profile</span>
                  </div>
                  <div className="anatomy-row">
                    <span className="anatomy-label">Name</span>
                    <span className="anatomy-value">{anatomy.user.name}</span>
                  </div>
                  {anatomy.user.age && (
                    <div className="anatomy-row">
                      <span className="anatomy-label">Age</span>
                      <span className="anatomy-value">{anatomy.user.age}</span>
                    </div>
                  )}
                  {anatomy.ageCalibration && (
                    <div className="anatomy-row">
                      <span className="anatomy-label">Age Calibration</span>
                      <span className="anatomy-value">{anatomy.ageCalibration}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Numerology */}
              {anatomy.numerology && (
                <div className="anatomy-section">
                  <div className="anatomy-section-header">
                    <svg className="anatomy-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="4" y="4" width="16" height="16" rx="2" />
                      <path d="M9 9h.01M15 9h.01M9 15h.01M15 15h.01" />
                    </svg>
                    <span className="anatomy-section-title">Numerology</span>
                  </div>
                  <div className="anatomy-row" style={{ marginBottom: 8 }}>
                    <span className="anatomy-label">Grid</span>
                    <div className="anatomy-grid-numbers">
                      {anatomy.numerology.mainGrid.map((n, i) => (
                        <span key={i} className="anatomy-grid-num">{n}</span>
                      ))}
                    </div>
                  </div>
                  {anatomy.numerology.lifePhase && (
                    <div className="anatomy-row">
                      <span className="anatomy-label">Life Phase</span>
                      <span className="anatomy-value">{anatomy.numerology.lifePhase}</span>
                    </div>
                  )}
                  <div className="anatomy-qualities">
                    {anatomy.numerology.keyQualities?.core?.length > 0 && (
                      <div className="anatomy-quality-group">
                        <div className="anatomy-quality-label">Core (50%) — identity foundation</div>
                        <div className="anatomy-quality-text">
                          {anatomy.numerology.keyQualities.core.join(' | ')}
                        </div>
                      </div>
                    )}
                    {anatomy.numerology.keyQualities?.professional && (
                      ['L1', 'L2', 'L3'].some(t => anatomy.numerology.keyQualities.professional[t]?.length > 0)
                    ) && (
                      <div className="anatomy-quality-group">
                        <div className="anatomy-quality-label">Professional (work / external life)</div>
                        {[
                          { tier: 'L1', pct: '90%', hint: 'daily expression' },
                          { tier: 'L2', pct: '70%', hint: 'situational' },
                          { tier: 'L3', pct: '50%', hint: 'latent' }
                        ].map(({ tier, pct, hint }) => {
                          const items = anatomy.numerology.keyQualities.professional[tier];
                          if (!items?.length) return null;
                          return (
                            <div key={tier} style={{ marginTop: 6 }}>
                              <div className="anatomy-quality-label" style={{ marginBottom: 2, opacity: 0.85 }}>
                                {tier} · {pct} · {hint}
                              </div>
                              <div className="anatomy-quality-text">
                                {items.join(' | ')}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {anatomy.numerology.keyQualities?.personal && (
                      ['L1', 'L2', 'L3'].some(t => anatomy.numerology.keyQualities.personal[t]?.length > 0)
                    ) && (
                      <div className="anatomy-quality-group">
                        <div className="anatomy-quality-label">Personal (relationships / inner life)</div>
                        {[
                          { tier: 'L1', pct: '90%', hint: 'daily expression' },
                          { tier: 'L2', pct: '70%', hint: 'situational' },
                          { tier: 'L3', pct: '50%', hint: 'latent' }
                        ].map(({ tier, pct, hint }) => {
                          const items = anatomy.numerology.keyQualities.personal[tier];
                          if (!items?.length) return null;
                          return (
                            <div key={tier} style={{ marginTop: 6 }}>
                              <div className="anatomy-quality-label" style={{ marginBottom: 2, opacity: 0.85 }}>
                                {tier} · {pct} · {hint}
                              </div>
                              <div className="anatomy-quality-text">
                                {items.join(' | ')}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Psychometric */}
              {anatomy.psychometric && (
                <div className="anatomy-section">
                  <div className="anatomy-section-header">
                    <svg className="anatomy-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
                    </svg>
                    <span className="anatomy-section-title">Psychometric Profile</span>
                  </div>
                  {Object.entries(anatomy.psychometric.dimensions || {}).map(([dim, val]) => (
                    <div key={dim} className="anatomy-dim-item">
                      <span className="anatomy-dim-name">{dim}</span>
                      <span className="anatomy-dim-value">{val}</span>
                    </div>
                  ))}
                  {anatomy.psychometric.narrativeSummary && (
                    <div className="anatomy-narrative">
                      {anatomy.psychometric.narrativeSummary}
                    </div>
                  )}
                  {/* Admin: confidence scores */}
                  {isAdmin && raw?.psychometric?.confidence && (
                    <div className="anatomy-admin-section">
                      <div className="anatomy-quality-label">Confidence Scores</div>
                      {Object.entries(raw.psychometric.confidence).map(([dim, conf]) => (
                        <div key={dim} className="anatomy-row" style={{ alignItems: 'center' }}>
                          <span className="anatomy-label" style={{ fontSize: 11 }}>{dim}</span>
                          <div className="anatomy-confidence-bar">
                            <div className="anatomy-confidence-track">
                              <div
                                className="anatomy-confidence-fill"
                                style={{ width: `${Math.round((conf || 0) * 100)}%` }}
                              />
                            </div>
                            <span className="anatomy-confidence-pct">
                              {Math.round((conf || 0) * 100)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Persona */}
              {anatomy.persona && Object.values(anatomy.persona).some(v => v) && (
                <div className="anatomy-section">
                  <div className="anatomy-section-header">
                    <svg className="anatomy-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5z" />
                      <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                    <span className="anatomy-section-title">What Elo Learned</span>
                  </div>
                  {anatomy.persona.strengths && (
                    <div className="anatomy-persona-item">
                      <div className="anatomy-persona-key">Observed Strengths</div>
                      <div className="anatomy-persona-val">{anatomy.persona.strengths}</div>
                    </div>
                  )}
                  {anatomy.persona.attachmentStyle && (
                    <div className="anatomy-persona-item">
                      <div className="anatomy-persona-key">Attachment Style</div>
                      <div className="anatomy-persona-val">{anatomy.persona.attachmentStyle}</div>
                    </div>
                  )}
                  {anatomy.persona.emotionalPatterns && (
                    <div className="anatomy-persona-item">
                      <div className="anatomy-persona-key">Emotional Patterns</div>
                      <div className="anatomy-persona-val">{anatomy.persona.emotionalPatterns}</div>
                    </div>
                  )}
                  {anatomy.persona.motivationalDrivers && (
                    <div className="anatomy-persona-item">
                      <div className="anatomy-persona-key">Motivational Drivers</div>
                      <div className="anatomy-persona-val">{anatomy.persona.motivationalDrivers}</div>
                    </div>
                  )}
                  {anatomy.persona.changeStage && (
                    <div className="anatomy-persona-item">
                      <div className="anatomy-persona-key">Change Readiness</div>
                      <div className="anatomy-persona-val">{anatomy.persona.changeStage}</div>
                    </div>
                  )}
                  {anatomy.persona.expressionStyle && (
                    <div className="anatomy-persona-item">
                      <div className="anatomy-persona-key">Communication Style</div>
                      <div className="anatomy-persona-val">{anatomy.persona.expressionStyle}</div>
                    </div>
                  )}
                  {anatomy.persona.keyInsights && (
                    <div className="anatomy-persona-item">
                      <div className="anatomy-persona-key">Key Insights</div>
                      <div className="anatomy-persona-val">{anatomy.persona.keyInsights}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Life Context */}
              {anatomy.lifeContext && (
                <div className="anatomy-section">
                  <div className="anatomy-section-header">
                    <svg className="anatomy-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                    </svg>
                    <span className="anatomy-section-title">Life Snapshot</span>
                  </div>
                  {anatomy.lifeContext.trend && (
                    <div style={{ marginBottom: 8 }}>
                      <span className={`anatomy-trend ${anatomy.lifeContext.trend}`}>
                        {anatomy.lifeContext.trend === 'improving' ? '↑' : anatomy.lifeContext.trend === 'declining' ? '↓' : '→'}
                        {' '}Trend: {anatomy.lifeContext.trend}
                      </span>
                    </div>
                  )}
                  {anatomy.lifeContext.recentStates?.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div className="anatomy-quality-label">Recent States</div>
                      {anatomy.lifeContext.recentStates.map((s, i) => (
                        <div key={i} className="anatomy-life-item">
                          <strong>{s.emotional || '?'}</strong> / Energy: {s.energy || '?'} / Motivation: {s.motivation || '?'}
                        </div>
                      ))}
                    </div>
                  )}
                  {anatomy.lifeContext.activeGoals?.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div className="anatomy-quality-label">Active Goals</div>
                      {anatomy.lifeContext.activeGoals.map((g, i) => (
                        <div key={i} className="anatomy-life-item">
                          {g.goal} <span style={{ color: 'var(--text-tertiary)' }}>({g.category})</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {anatomy.lifeContext.activeHabits?.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div className="anatomy-quality-label">Active Habits</div>
                      {anatomy.lifeContext.activeHabits.map((h, i) => (
                        <div key={i} className="anatomy-life-item">
                          {h.commitment} <span style={{ color: 'var(--text-tertiary)' }}>({h.frequency}, streak: {h.streak}d)</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {anatomy.lifeContext.patterns?.length > 0 && (
                    <div>
                      <div className="anatomy-quality-label">Detected Patterns</div>
                      {anatomy.lifeContext.patterns.map((p, i) => (
                        <div key={i} className="anatomy-life-item">
                          {p.type} <span style={{ color: 'var(--text-tertiary)' }}>({Math.round((p.confidence || 0) * 100)}% confidence)</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Session — compact, glanceable progress card */}
              {anatomy.session && (
                <div className="anatomy-section">
                  <div className="anatomy-section-header">
                    <svg className="anatomy-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    <span className="anatomy-section-title">Session Snapshot</span>
                  </div>
                  {(() => {
                    const elapsed = anatomy.session.elapsedMinutes || 0;
                    const cap = 15;
                    const pct = Math.min(100, Math.round((elapsed / cap) * 100));
                    const phase = elapsed < 5 ? 'Opening' : elapsed < 12 ? 'Working' : elapsed < 15 ? 'Closing' : 'Wrap-up';
                    return (
                      <>
                        <div className="anatomy-session-meta">
                          <span className="anatomy-session-elapsed">{elapsed} min</span>
                          <span className={`anatomy-session-phase phase-${phase.toLowerCase()}`}>{phase}</span>
                          {anatomy.session.shouldWrapup && (
                            <span className="anatomy-session-flag">Wrap-up active</span>
                          )}
                        </div>
                        <div className="anatomy-session-bar">
                          <div className="anatomy-session-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                        {anatomy.session.initialEmotion && (
                          <div className="anatomy-session-opener">
                            <span className="anatomy-session-opener-label">Opened on</span>
                            <span className="anatomy-session-opener-value">"{anatomy.session.initialEmotion}"</span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Contact — surfaces the same numerology/EI signals the model sees */}
              {anatomy.contact && (
                <div className="anatomy-section">
                  <div className="anatomy-section-header">
                    <svg className="anatomy-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    <span className="anatomy-section-title">Contact Intelligence</span>
                  </div>
                  <div className="anatomy-contact-head">
                    <div className="anatomy-contact-name">
                      {anatomy.contact.name}
                      {anatomy.contact.relation && (
                        <span className="anatomy-contact-relation">{anatomy.contact.relation}</span>
                      )}
                    </div>
                    <div className="anatomy-contact-signals">
                      <span className={`anatomy-signal ${anatomy.contact.hasNumerology ? 'on' : 'off'}`}>
                        <span className="anatomy-signal-dot" /> Numerology
                      </span>
                      <span className={`anatomy-signal ${anatomy.contact.hasAnonymizedEI ? 'on' : 'off'}`}>
                        <span className="anatomy-signal-dot" /> Emotional intel
                      </span>
                    </div>
                  </div>
                  {anatomy.contact.numerology ? (
                    <>
                      <div className="anatomy-contact-grid-row">
                        <span className="anatomy-label">Grid</span>
                        <div className="anatomy-grid-numbers">
                          {anatomy.contact.numerology.mainGrid.map((n, i) => (
                            <span key={i} className="anatomy-grid-num">{n}</span>
                          ))}
                        </div>
                        {anatomy.contact.numerology.age != null && (
                          <span className="anatomy-contact-age">{anatomy.contact.numerology.age} yrs</span>
                        )}
                      </div>
                      {anatomy.contact.numerology.lifePhase && (
                        <div className="anatomy-contact-phase">{anatomy.contact.numerology.lifePhase}</div>
                      )}
                      {anatomy.contact.numerology.keyQualities?.core?.length > 0 && (
                        <div className="anatomy-quality-group">
                          <div className="anatomy-quality-label">Core — identity foundation</div>
                          <div className="anatomy-quality-text">
                            {anatomy.contact.numerology.keyQualities.core.join(' | ')}
                          </div>
                        </div>
                      )}
                      {anatomy.contact.numerology.keyQualities?.personal?.L1?.length > 0 && (
                        <div className="anatomy-quality-group">
                          <div className="anatomy-quality-label">Personal · daily expression</div>
                          <div className="anatomy-quality-text">
                            {anatomy.contact.numerology.keyQualities.personal.L1.join(' | ')}
                          </div>
                        </div>
                      )}
                      {anatomy.contact.numerology.keyQualities?.professional?.L1?.length > 0 && (
                        <div className="anatomy-quality-group">
                          <div className="anatomy-quality-label">Professional · daily expression</div>
                          <div className="anatomy-quality-text">
                            {anatomy.contact.numerology.keyQualities.professional.L1.join(' | ')}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="anatomy-contact-empty">
                      Add a date of birth to this contact to unlock numerology-grounded insight.
                    </div>
                  )}
                </div>
              )}

              {/* Team — composition + per-member quick profile */}
              {anatomy.team && (
                <div className="anatomy-section">
                  <div className="anatomy-section-header">
                    <svg className="anatomy-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    <span className="anatomy-section-title">Team Intelligence</span>
                  </div>
                  <div className="anatomy-team-head">
                    <span className="anatomy-team-name">{anatomy.team.name}</span>
                    <span className="anatomy-team-count">{anatomy.team.memberCount} members</span>
                  </div>
                  {Array.isArray(anatomy.team.members) && anatomy.team.members.length > 0 ? (
                    <div className="anatomy-team-list">
                      {anatomy.team.members.map((m, i) => (
                        <div key={i} className="anatomy-team-member">
                          <div className="anatomy-team-member-top">
                            <span className="anatomy-team-member-name">{m.name}</span>
                            {m.relation && <span className="anatomy-team-member-rel">{m.relation}</span>}
                            {m.grid && (
                              <span className="anatomy-team-member-grid">{m.grid.join('-')}</span>
                            )}
                          </div>
                          {m.core?.length > 0 && (
                            <div className="anatomy-team-member-traits">{m.core.join(' · ')}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="anatomy-contact-empty">
                      No member profiles available yet.
                    </div>
                  )}
                </div>
              )}

              {/* Admin Raw JSON */}
              {isAdmin && raw && (
                <div className="anatomy-section">
                  <button
                    className="anatomy-admin-toggle"
                    onClick={() => setShowRaw(!showRaw)}
                  >
                    {showRaw ? '▾ Hide' : '▸ Show'} Raw Metadata (Admin)
                  </button>
                  {showRaw && (
                    <div className="anatomy-raw-json">
                      {JSON.stringify(raw, null, 2)}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AnatomyPanel;
